import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api, { formatINR } from "../lib/api";
import { FileText, MapPin, AlertCircle, Eye, ShoppingBag, Briefcase } from "lucide-react";
import { Toaster, toast } from "sonner";

/**
 * Standalone packing-slips viewer for roles that don't do packing themselves
 * (admin / editor / accounts / MR). MR sees only slips matching their territory.
 */
export default function PackingSlips() {
    const location = useLocation();
    const nav = useNavigate();
    const { user } = useAuth();
    const inAdmin = location.pathname.startsWith("/admin");
    const slipPathBase = inAdmin ? "/admin/packing-slips" : "/employee/packing-slips";

    // Client-side gate — mirrors backend _slip_viewer_scope rule. Prevents non-eligible
    // employees who deep-link from staring at an empty shell. Backend remains the source
    // of truth for security; this is purely cosmetic.
    useEffect(() => {
        if (!user) return;
        if (user.role === "admin" || user.role === "editor") return;
        if (user.role !== "employee") { nav("/", { replace: true }); return; }
        const caps = user.capabilities || [];
        const isAB = user.designation_name === "Accounts & Billing";
        if (!(isAB || caps.includes("packing") || caps.includes("mr") || caps.includes("admin"))) {
            toast.error("You don't have access to packing slips.");
            nav("/employee", { replace: true });
        }
    }, [user, nav]);

    const [tab, setTab] = useState("all"); // all | retail | b2b
    const [data, setData] = useState({ rows: [], totals: { count: 0, retail: 0, b2b: 0 }, scope: { scope: "all" } });
    const [loading, setLoading] = useState(false);
    const [q, setQ] = useState("");

    const load = async (orderType) => {
        setLoading(true);
        try {
            const params = orderType === "all" ? {} : { order_type: orderType };
            const { data } = await api.get("/packing/slips", { params });
            setData(data);
        } catch { /* surfaced by axios interceptor */ }
        finally { setLoading(false); }
    };

    useEffect(() => { load(tab); }, [tab]);

    const filtered = data.rows.filter((r) => {
        if (!q.trim()) return true;
        const needle = q.toLowerCase();
        return (r.customer_name || "").toLowerCase().includes(needle)
            || (r.state || "").toLowerCase().includes(needle)
            || (r.city || "").toLowerCase().includes(needle)
            || r.order_short.toLowerCase().includes(needle)
            || (r.customer_phone || "").includes(needle);
    });

    const TABS = [
        { key: "all", label: "All slips", count: data.totals.count },
        { key: "retail", label: "Retail", count: data.totals.retail, icon: ShoppingBag },
        { key: "b2b", label: "B2B", count: data.totals.b2b, icon: Briefcase },
    ];

    return (
        <div className="space-y-5" data-testid="packing-slips-page">
            <Toaster position="top-center" />
            <div>
                <div className="label-eyebrow text-sky-700">Warehouse · finalized</div>
                <h1 className="font-serif text-4xl text-stone-900">Packing slips</h1>
                <p className="text-sm text-stone-500 mt-1">Every packing slip created by Packing &amp; Dispatch lands here. Use it to verify dispatch, follow up on out-of-stock items, and proceed to billing.</p>
            </div>

            {data.scope?.scope === "mr_territory" && (
                <div className="bg-sky-50 border border-sky-200 px-4 py-3 text-sm text-sky-900 flex items-start gap-2" data-testid="mr-territory-banner">
                    <MapPin className="w-4 h-4 mt-0.5" />
                    <div>
                        Showing slips shipping to your assigned territory: <strong>{(data.scope.mr_states || []).join(", ") || "(none assigned)"}</strong>.
                        {(data.scope.mr_states || []).length === 0 && " Ask your admin to assign at least one state on your profile."}
                    </div>
                </div>
            )}

            {/* Tabs */}
            <div className="flex flex-wrap gap-1 border-b border-stone-200" data-testid="slips-tabs">
                {TABS.map((t) => {
                    const isActive = tab === t.key;
                    return (
                        <button key={t.key} onClick={() => setTab(t.key)} data-testid={`slips-tab-${t.key}`}
                            className={`px-4 py-3 -mb-px flex items-center gap-2 text-sm border-b-2 ${isActive ? "border-sky-700 text-sky-700 font-semibold bg-white" : "border-transparent text-stone-500 hover:text-stone-900 hover:bg-stone-50"}`}>
                            {t.icon && <t.icon className="w-4 h-4" />}
                            {t.label}
                            <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 ${isActive ? "bg-sky-100 text-sky-700" : "bg-stone-200 text-stone-600"}`}>{t.count}</span>
                        </button>
                    );
                })}
            </div>

            {/* Search */}
            <div className="flex flex-wrap items-center gap-2">
                <input
                    type="text"
                    placeholder="Search by customer, state, city, phone or order #…"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    className="flex-1 min-w-[240px] h-9 px-3 border border-stone-300 rounded-none text-sm bg-white"
                    data-testid="slips-search"
                />
                <span className="text-xs text-stone-500">Showing {filtered.length} of {data.rows.length}</span>
            </div>

            {/* Table */}
            {loading ? (
                <div className="text-sm text-stone-500 text-center py-12">Loading slips…</div>
            ) : filtered.length === 0 ? (
                <div className="bg-white border border-stone-200 p-12 text-center text-sm text-stone-500">
                    <FileText className="w-8 h-8 mx-auto mb-2 text-stone-300" />
                    No packing slips yet{tab !== "all" ? ` (${tab})` : ""}{data.scope?.scope === "mr_territory" ? " in your territory" : ""}.
                </div>
            ) : (
                <div className="bg-white border border-stone-200 overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-stone-50 border-b border-stone-200">
                                <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Order</th>
                                <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Type</th>
                                <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Customer</th>
                                <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Ships to</th>
                                <th className="text-right p-3 text-xs uppercase tracking-widest text-stone-500">Packed / Ordered</th>
                                <th className="text-right p-3 text-xs uppercase tracking-widest text-stone-500">OOS</th>
                                <th className="text-right p-3 text-xs uppercase tracking-widest text-stone-500">Total</th>
                                <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Packed at</th>
                                <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Billing</th>
                                <th className="text-right p-3 text-xs uppercase tracking-widest text-stone-500">Slip</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((r) => (
                                <tr key={r.id} className="border-b border-stone-100 hover:bg-sky-50/40" data-testid={`slips-row-${r.id}`}>
                                    <td className="p-3 font-mono text-xs">#{r.order_short}</td>
                                    <td className="p-3"><span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 ${r.is_b2b ? "bg-indigo-100 text-indigo-700" : "bg-sky-100 text-sky-700"}`}>{r.order_type}</span></td>
                                    <td className="p-3">
                                        <div className="font-medium text-stone-900">{r.customer_name}</div>
                                        {r.customer_phone && <div className="text-[10px] text-stone-500">{r.customer_phone}</div>}
                                    </td>
                                    <td className="p-3 text-xs text-stone-600">
                                        {r.city ? <span>{r.city}, </span> : null}<span className="font-medium">{r.state || "—"}</span>
                                    </td>
                                    <td className="p-3 text-right font-mono">{r.items_packed_qty} / {r.items_ordered_qty}</td>
                                    <td className="p-3 text-right">
                                        {r.oos_lines > 0
                                            ? <span className="inline-flex items-center gap-1 text-red-700 font-semibold"><AlertCircle className="w-3 h-3" />{r.oos_lines}</span>
                                            : <span className="text-stone-400">—</span>}
                                    </td>
                                    <td className="p-3 text-right font-mono text-emerald-700">{formatINR(r.total)}</td>
                                    <td className="p-3 text-[11px] text-stone-500">{r.packed_at?.slice(0, 16).replace("T", " ")}<div className="text-[10px] text-stone-400">by {r.packed_by_name || "—"}</div></td>
                                    <td className="p-3"><span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 ${r.billing_status === "paid" ? "bg-emerald-100 text-emerald-800" : r.billing_status === "invoiced" ? "bg-sky-100 text-sky-700" : "bg-stone-100 text-stone-600"}`}>{r.billing_status}</span></td>
                                    <td className="p-3 text-right">
                                        <Link to={`${slipPathBase}/${r.id}`} className="inline-flex items-center gap-1 text-sky-700 hover:underline text-xs" data-testid={`slips-view-${r.id}`}>
                                            <Eye className="w-3.5 h-3.5" />View
                                        </Link>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
